import React, { useEffect, useRef, useState } from 'react';
import type {
  WorkerRequest,
  WorkerResponse,
  InitViewportPayload,
  ResizeViewportPayload,
  CameraPointerDownPayload,
  CameraPointerMovePayload,
  CameraWheelPayload,
  GeometryPointerPayload,
  SetConstructionGeometryPayload,
  GeometryMovedPayload,
  SetCfdSetupPayload,
  SetFlowPreviewPayload
} from '@runtime/types';
import { useAppDispatch, useAppSelector } from '@state/store';
import {
  addRectangle,
  addGeometry,
  deleteGeometry,
  moveGeometry,
  selectGeometry,
  setActiveTool,
  ConstructionTool
} from '@state/slices/geometrySlice';
import {
  setShowDomainBox,
  setCfdVisible,
  setCfdDensity,
  setCfdGlyphScale,
  toggleFlowPreview
} from '@state/slices/uiSlice';
import {
  toggleProbeMode,
  setActiveProbe,
  setDiagnostics,
  setVerificationSamples,
  setSelectedRunId
} from '@state/slices/probeSlice';
import type {
  RectangleGeometry,
  BuildingMassGeometry,
  CfdFieldProbe,
  CfdResult
} from '@contracts/index';
import {
  computeCfdResultDiagnostics,
  verifyCfdSampleEquivalence,
  validateCfdResultIntegrity
} from '@contracts/index';
import { EngineRegistry } from '@services/engine';
import {
  AIRFLOW_LEGEND_GRADIENT,
  AIRFLOW_MIN_SPEED_RATIO,
  AIRFLOW_MAX_SPEED_RATIO
} from '../../workers/flowPreviewRenderer';

/**
 * ViewportCanvas — Clean Hardware Host Surface for Scientific Viewport
 *
 * ARCHITECTURAL CONTRACT:
 * - Dynamic canvas lifecycle compatible with React StrictMode (fresh canvas per mount).
 * - Direct offscreen canvas transfer to dedicated Visualization Worker.
 * - Zero GPU allocations or render loops in React.
 * - Raw pointer interaction forwarded directly to worker via message port.
 * - React does NOT participate in high-frequency pointer motion (zero setState on move).
 * - Full pointer capture support for seamless dragging outside viewport boundaries.
 * - Dual mode pointer routing: Left click/drag creates geometry when rectangle tool is active,
 *   while Orbit/Pan/Dolly camera navigation remains 100% available via middle/right click, wheel, or modifiers.
 */
export const ViewportCanvas: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const workerRef = useRef<Worker | null>(null);

  const dispatch = useAppDispatch();
  const activeTool = useAppSelector((state) => state.geometry.activeTool);
  const geometryEntities = useAppSelector((state) => state.geometry.entities);
  const selectedGeometryId = useAppSelector((state) => state.geometry.selectedId);

  const activeScenarioId = useAppSelector((state) => state.study.study.activeScenarioId);
  const activeScenario = useAppSelector((state) => state.study.study.scenarios[activeScenarioId]);
  const cfdSetup = activeScenario?.cfdSetup;
  const showDomainBox = useAppSelector((state) => state.ui.showDomainBox ?? true);
  const cfdVisible = useAppSelector((state) => state.ui.cfdVisible ?? true);
  const cfdDensity = useAppSelector((state) => state.ui.cfdDensity ?? 1.0);
  const cfdGlyphScale = useAppSelector((state) => state.ui.cfdGlyphScale ?? 1.0);
  const flowPreviewActive = useAppSelector((state) => state.ui.flowPreviewActive ?? false);

  const runs = useAppSelector((state) => state.run.runs);
  const activeRunId = useAppSelector((state) => state.run.activeRunId);
  const runOrder = useAppSelector((state) => state.run.runOrder);
  const activeRun = activeRunId ? runs[activeRunId] : runOrder.length > 0 ? runs[runOrder[0]] : null;

  // Last Valid Result Rule (Gate 3.2):
  // If activeRun is COMPLETED and has result, display it.
  // If activeRun is FAILED/RUNNING/QUEUED, preserve and display the latest completed valid run.
  const displayRun = (activeRun && activeRun.status === 'COMPLETED' && activeRun.resultStatus === 'AVAILABLE')
    ? activeRun
    : runOrder.map((id) => runs[id]).find((r) => r && r.status === 'COMPLETED' && r.resultStatus === 'AVAILABLE') || null;

  const isProbeModeActive = useAppSelector((state) => state.probe.isProbeModeActive);
  const activeProbe = useAppSelector((state) => state.probe.activeProbe);
  const diagnostics = useAppSelector((state) => state.probe.diagnostics);

  // Preview-only interactive wind state (Gate 03)
  // Decoupled from authoritative Scenario/CFD configuration. Discarded on preview exit.
  const initialWindSpeed = displayRun?.specification?.wind?.speed ?? cfdSetup?.wind?.speed ?? 5.0;
  const initialWindDirection = displayRun?.specification?.wind?.direction ?? cfdSetup?.wind?.direction ?? 25.0;

  const [previewWindSpeed, setPreviewWindSpeed] = useState<number>(initialWindSpeed);
  const [previewWindDirection, setPreviewWindDirection] = useState<number>(initialWindDirection);
  const [previewFlowDensity, setPreviewFlowDensity] = useState<number>(1500);

  // Re-synchronize preview wind from authoritative run when Flow Preview mode is entered
  useEffect(() => {
    if (flowPreviewActive) {
      const speed = displayRun?.specification?.wind?.speed ?? cfdSetup?.wind?.speed ?? 5.0;
      const dir = displayRun?.specification?.wind?.direction ?? cfdSetup?.wind?.direction ?? 25.0;
      setPreviewWindSpeed(speed);
      setPreviewWindDirection(dir);
    }
  }, [flowPreviewActive, displayRun?.runId]);

  // Add building obstacle to interactive preview
  const handleAddBuilding = () => {
    const id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? `bldg_${crypto.randomUUID()}`
      : `bldg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    const existingCount = Object.keys(geometryEntities).length;
    const spacing = 15;
    const col = existingCount % 4;
    const row = Math.floor(existingCount / 4) % 4;
    const offsetX = (col - 1.5) * spacing;
    const offsetZ = (row - 1.5) * spacing;

    const newBuilding: BuildingMassGeometry = {
      id,
      type: 'building_mass',
      center: { x: offsetX, y: 0, z: offsetZ },
      width: 5,
      depth: 5,
      height: 5,
      rotationY: 0
    };

    dispatch(addGeometry(newBuilding));
  };

  // Keyboard shortcut to remove selected geometry (Delete / Backspace)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedGeometryId) {
        const activeTag = (document.activeElement?.tagName || '').toLowerCase();
        if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') return;
        dispatch(deleteGeometry(selectedGeometryId));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedGeometryId, dispatch]);

  const activeToolRef = useRef<ConstructionTool>(activeTool);
  activeToolRef.current = activeTool;

  const isProbeModeActiveRef = useRef(isProbeModeActive);
  isProbeModeActiveRef.current = isProbeModeActive;

  // Synchronize Active Probe to Visualization Worker
  useEffect(() => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({
      id: 'sync-active-probe',
      type: 'SET_ACTIVE_PROBE',
      payload: { probe: activeProbe }
    });
  }, [activeProbe]);

  // Synchronize CFD field visibility to Visualization Worker (Gate 4.2)
  useEffect(() => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({
      id: 'sync-cfd-visibility',
      type: 'SET_CFD_FIELD_VISIBILITY',
      payload: { visible: cfdVisible }
    });
  }, [cfdVisible]);

  // Synchronize CFD display options (density, glyph scale) to Visualization Worker (Gate 4.2)
  useEffect(() => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({
      id: 'sync-cfd-display-options',
      type: 'SET_CFD_DISPLAY_OPTIONS',
      payload: { density: cfdDensity, glyphScale: cfdGlyphScale }
    });
  }, [cfdDensity, cfdGlyphScale]);

  // Synchronize interactive flow preview mode, preview wind, and flow density to Visualization Worker
  useEffect(() => {
    if (!workerRef.current) return;
    const msg: WorkerRequest<SetFlowPreviewPayload> = {
      id: 'sync-flow-preview',
      type: 'SET_FLOW_PREVIEW',
      payload: {
        active: flowPreviewActive,
        speed: flowPreviewActive ? previewWindSpeed : undefined,
        direction: flowPreviewActive ? previewWindDirection : undefined,
        density: flowPreviewActive ? previewFlowDensity : undefined
      }
    };
    workerRef.current.postMessage(msg);
  }, [flowPreviewActive, previewWindSpeed, previewWindDirection, previewFlowDensity]);

  // Adjust cursor when probe mode is toggled
  useEffect(() => {
    if (!containerRef.current) return;
    const canvas = containerRef.current.querySelector('canvas');
    if (canvas) {
      canvas.style.cursor = isProbeModeActive
        ? 'crosshair'
        : activeTool === 'rectangle'
          ? 'crosshair'
          : 'grab';
    }
  }, [isProbeModeActive, activeTool]);


  // Synchronize committed geometry from Redux to Visualization Worker
  useEffect(() => {
    if (!workerRef.current) return;
    const items = Object.values(geometryEntities);
    const syncMsg: WorkerRequest<SetConstructionGeometryPayload> = {
      id: 'sync-construction-geometry',
      type: 'SET_CONSTRUCTION_GEOMETRY',
      payload: {
        items,
        selectedId: selectedGeometryId
      }
    };
    workerRef.current.postMessage(syncMsg);
  }, [geometryEntities, selectedGeometryId]);

  // Synchronize CFD setup (wind, domain dimensions, and box visibility) from Redux to Visualization Worker
  useEffect(() => {
    if (!workerRef.current || !cfdSetup) return;
    const msg: WorkerRequest<SetCfdSetupPayload> = {
      id: 'sync-cfd-setup',
      type: 'SET_CFD_SETUP',
      payload: {
        wind: cfdSetup.wind,
        domain: cfdSetup.domain,
        showDomainBox
      }
    };
    workerRef.current.postMessage(msg);
  }, [
    cfdSetup?.wind.speed,
    cfdSetup?.wind.direction,
    cfdSetup?.domain.width,
    cfdSetup?.domain.length,
    cfdSetup?.domain.height,
    showDomainBox
  ]);

  // Synchronize Authoritative CFD Result to Visualization Worker
  // Enforces Last Valid Result Rule: A newer failed/queued run does NOT destroy the last valid result.
  useEffect(() => {
    if (!workerRef.current) return;
    if (!displayRun) {
      workerRef.current.postMessage({
        id: 'clear-cfd-result',
        type: 'INGEST_NORMALIZED_CFD_RESULT',
        payload: {
          result: null
        }
      });
      dispatch(setDiagnostics(null));
      dispatch(setVerificationSamples([]));
      dispatch(setSelectedRunId(null));
      dispatch(setActiveProbe(null));
      return;
    }

    const adapter = EngineRegistry.getAdapter(displayRun.engine);
    if (!adapter) return;

    let isCancelled = false;
    adapter.getResult(displayRun.runId).then((result: CfdResult | null) => {
      if (isCancelled || !result || !workerRef.current) return;

      // G3.1 Integrity validation check before passing to viewport
      const validation = validateCfdResultIntegrity(result);
      if (!validation.isValid) {
        console.warn(`[ViewportCanvas] Retrieved result for ${displayRun.runId} failed integrity validation:`, validation.errors);
        return;
      }

      workerRef.current.postMessage({
        id: `ingest-result-${displayRun.runId}`,
        type: 'INGEST_NORMALIZED_CFD_RESULT',
        payload: { result }
      });

      // Compute exact scientific diagnostics from authoritative CFD result
      const diag = computeCfdResultDiagnostics(result);
      dispatch(setDiagnostics(diag));

      // Extract raw -> normalized verification samples to prove physical field preservation
      const samples = verifyCfdSampleEquivalence(result);
      dispatch(setVerificationSamples(samples));
      dispatch(setSelectedRunId(result.runId));
    }).catch((err: unknown) => {
      console.warn('[ViewportCanvas] Failed to retrieve and ingest CFD result:', err);
    });

    return () => {
      isCancelled = true;
    };
  }, [displayRun?.runId, displayRun?.status, displayRun?.resultStatus]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create fresh canvas element on each mount to prevent
    // 'Cannot transfer control from a canvas that has already transferred control'
    // under React StrictMode / HMR.
    const canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    canvas.style.outline = 'none';
    canvas.style.touchAction = 'none';
    canvas.style.cursor = activeToolRef.current === 'rectangle' ? 'crosshair' : 'grab';
    container.appendChild(canvas);

    if (typeof canvas.transferControlToOffscreen !== 'function') {
      console.warn('[ViewportCanvas] transferControlToOffscreen is not supported');
      return () => {
        if (container.contains(canvas)) container.removeChild(canvas);
      };
    }

    let offscreen: OffscreenCanvas;
    try {
      offscreen = canvas.transferControlToOffscreen();
    } catch (err) {
      console.warn('[ViewportCanvas] transferControlToOffscreen failed:', err);
      return () => {
        if (container.contains(canvas)) container.removeChild(canvas);
      };
    }

    // Spawn dedicated Visualization Worker
    const worker = new Worker(
      new URL('../../workers/visualization.worker.ts', import.meta.url),
      { type: 'module' }
    );
    workerRef.current = worker;

    // Listen for events emitted by the worker (e.g., GEOMETRY_CREATED, GEOMETRY_SELECTED, GEOMETRY_MOVED, CFD_FIELD_PROBED)
    worker.onmessage = (event: MessageEvent<WorkerResponse<unknown>>) => {
      const { type, payload } = event.data as { type?: string; payload?: unknown };
      if (type === 'GEOMETRY_CREATED' && payload) {
        dispatch(addRectangle(payload as RectangleGeometry));
      } else if (type === 'GEOMETRY_SELECTED' && payload && typeof payload === 'object' && 'id' in payload) {
        dispatch(selectGeometry((payload as { id: string | null }).id));
      } else if (type === 'GEOMETRY_MOVED' && payload && typeof payload === 'object' && 'id' in payload && 'center' in payload) {
        const moved = payload as GeometryMovedPayload;
        dispatch(moveGeometry({ id: moved.id, center: moved.center }));
      } else if (type === 'CFD_FIELD_PROBED' && payload) {
        dispatch(setActiveProbe(payload as CfdFieldProbe));
      }
    };

    const rect = container.getBoundingClientRect();
    const width = Math.max(rect.width, 100);
    const height = Math.max(rect.height, 100);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const initMsg: WorkerRequest<InitViewportPayload> = {
      id: 'init-viewport',
      type: 'INIT_VIEWPORT',
      payload: {
        canvas: offscreen,
        width,
        height,
        dpr
      }
    };
    worker.postMessage(initMsg, [offscreen]);

    // Initial sync of existing geometry
    const initialItems = Object.values(geometryEntities);
    worker.postMessage({
      id: 'init-sync-geometry',
      type: 'SET_CONSTRUCTION_GEOMETRY',
      payload: {
        items: initialItems,
        selectedId: selectedGeometryId
      }
    });

    if (cfdSetup) {
      worker.postMessage({
        id: 'init-sync-cfd-setup',
        type: 'SET_CFD_SETUP',
        payload: {
          wind: cfdSetup.wind,
          domain: cfdSetup.domain,
          showDomainBox
        }
      });
    }

    if (displayRun) {
      const adapter = EngineRegistry.getAdapter(displayRun.engine);
      if (adapter) {
        adapter.getResult(displayRun.runId).then((result: CfdResult | null) => {
          if (result && workerRef.current) {
            const validation = validateCfdResultIntegrity(result);
            if (!validation.isValid) return;

            workerRef.current.postMessage({
              id: `init-ingest-result-${displayRun.runId}`,
              type: 'INGEST_NORMALIZED_CFD_RESULT',
              payload: { result }
            });
            const diag = computeCfdResultDiagnostics(result);
            dispatch(setDiagnostics(diag));
            const samples = verifyCfdSampleEquivalence(result);
            dispatch(setVerificationSamples(samples));
            dispatch(setSelectedRunId(result.runId));
          }
        });
      }
    }

    // Responsive viewport dimension tracking
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: w, height: h } = entry.contentRect;
        if (w > 0 && h > 0) {
          const currentDpr = Math.min(window.devicePixelRatio || 1, 2);
          const resizeMsg: WorkerRequest<ResizeViewportPayload> = {
            id: 'resize-viewport',
            type: 'RESIZE_VIEWPORT',
            payload: {
              width: w,
              height: h,
              dpr: currentDpr
            }
          };
          worker.postMessage(resizeMsg);
        }
      }
    });

    resizeObserver.observe(container);

    // -------------------------------------------------------------------------
    // Direct Pointer Event Routing
    // -------------------------------------------------------------------------
    let isCreatingGeometry = false;
    let isCameraDragging = false;
    const pointerDownPos = { x: 0, y: 0 };
    let pointerDownButton = 0;

    const handlePointerDown = (e: PointerEvent) => {
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // Ignore
      }

      const bounds = canvas.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const y = e.clientY - bounds.top;
      pointerDownPos.x = x;
      pointerDownPos.y = y;
      pointerDownButton = e.button;

      // Primary Left Click with no navigation modifiers creates geometry when rectangle tool is active
      const isPrimaryLeft = e.button === 0 && !e.altKey && !e.shiftKey;

      if (activeToolRef.current === 'rectangle' && isPrimaryLeft) {
        isCreatingGeometry = true;
        canvas.style.cursor = 'crosshair';

        const payload: GeometryPointerPayload = { x, y };
        worker.postMessage({
          id: 'geom-pointer-down',
          type: 'GEOMETRY_POINTER_DOWN',
          payload
        });
      } else {
        // Camera navigation (Orbit, Pan, Dolly)
        isCameraDragging = true;
        canvas.style.cursor = 'grabbing';

        const payload: CameraPointerDownPayload = {
          pointerId: e.pointerId,
          x,
          y,
          button: e.button,
          shift: e.shiftKey,
          ctrl: e.ctrlKey,
          alt: e.altKey
        };

        worker.postMessage({
          id: 'cam-pointer-down',
          type: 'CAMERA_POINTER_DOWN',
          payload
        });
      }
    };

    const handlePointerMove = (e: PointerEvent) => {
      const bounds = canvas.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const y = e.clientY - bounds.top;

      if (isCreatingGeometry) {
        const payload: GeometryPointerPayload = { x, y };
        worker.postMessage({
          id: 'geom-pointer-move',
          type: 'GEOMETRY_POINTER_MOVE',
          payload
        });
      } else if (isCameraDragging) {
        const payload: CameraPointerMovePayload = {
          pointerId: e.pointerId,
          x,
          y
        };

        worker.postMessage({
          id: 'cam-pointer-move',
          type: 'CAMERA_POINTER_MOVE',
          payload
        });
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      const bounds = canvas.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const y = e.clientY - bounds.top;

      try {
        if (canvas.hasPointerCapture(e.pointerId)) {
          canvas.releasePointerCapture(e.pointerId);
        }
      } catch {
        // Ignore
      }

      if (isCreatingGeometry) {
        isCreatingGeometry = false;
        canvas.style.cursor = activeToolRef.current === 'rectangle' ? 'crosshair' : 'grab';

        const payload: GeometryPointerPayload = { x, y };
        worker.postMessage({
          id: 'geom-pointer-up',
          type: 'GEOMETRY_POINTER_UP',
          payload
        });
      }

      if (isCameraDragging) {
        isCameraDragging = false;
        canvas.style.cursor = activeToolRef.current === 'rectangle' ? 'crosshair' : 'grab';

        worker.postMessage({
          id: 'cam-pointer-up',
          type: 'CAMERA_POINTER_UP',
          payload: { pointerId: e.pointerId }
        });

        // Click-to-select / Click-to-probe: If left button clicked with minimal motion
        const dist = Math.hypot(x - pointerDownPos.x, y - pointerDownPos.y);
        if (dist < 5 && pointerDownButton === 0 && !e.altKey && !e.shiftKey) {
          if (isProbeModeActiveRef.current) {
            worker.postMessage({
              id: `probe-click-${Date.now()}`,
              type: 'PROBE_CFD_FIELD',
              payload: {
                runId: displayRun?.runId || activeRun?.runId,
                screenX: x,
                screenY: y,
                point: [0, 0, 0]
              }
            });
          } else {
            worker.postMessage({
              id: 'geom-select-at',
              type: 'GEOMETRY_SELECT_AT',
              payload: { x, y }
            });
          }
        }
      }
    };

    const handlePointerCancel = (e: PointerEvent) => {
      handlePointerUp(e);
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const payload: CameraWheelPayload = {
        deltaY: e.deltaY
      };

      worker.postMessage({
        id: 'cam-wheel',
        type: 'CAMERA_WHEEL',
        payload
      });
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isCreatingGeometry) {
          isCreatingGeometry = false;
          canvas.style.cursor = activeToolRef.current === 'rectangle' ? 'crosshair' : 'grab';
          worker.postMessage({
            id: 'geom-cancel',
            type: 'GEOMETRY_CANCEL',
            payload: null
          });
        }
      }
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointercancel', handlePointerCancel);
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      resizeObserver.disconnect();

      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointercancel', handlePointerCancel);
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', handleKeyDown);

      worker.postMessage({
        id: 'stop-viewport',
        type: 'STOP_VIEWPORT',
        payload: null
      });
      worker.terminate();
      workerRef.current = null;

      if (container.contains(canvas)) {
        container.removeChild(canvas);
      }
    };
  }, [dispatch]);

  // Update cursor style when activeTool changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const canvas = container.querySelector('canvas');
    if (canvas) {
      canvas.style.cursor = activeTool === 'rectangle' ? 'crosshair' : 'grab';
    }
  }, [activeTool]);


  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: '#F2EFE9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* CAD/GIS Construction Tool Floating HUD */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 6,
          padding: '4px 8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          fontSize: '11px',
          fontWeight: 500,
          color: '#334155',
          userSelect: 'none'
        }}
      >
        {flowPreviewActive ? (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 8px',
                borderRadius: 4,
                background: '#E0F2FE',
                color: '#0369A1',
                fontWeight: 600
              }}
            >
              <span style={{ fontSize: '13px' }}>≋</span>
              <span>FLOW PREVIEW MODE</span>
            </div>

            <span
              style={{
                width: 1,
                height: 14,
                background: 'rgba(0, 0, 0, 0.1)',
                margin: '0 2px'
              }}
            />

            <button
              id="toolbar-flow-preview-btn"
              type="button"
              onClick={() => dispatch(toggleFlowPreview())}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 10px',
                borderRadius: 4,
                border: '1px solid #CBD5E1',
                background: '#FFFFFF',
                color: '#0F172A',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 600,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
              title="Return to Authoritative CFD Simulation View"
            >
              <span style={{ fontSize: '11px', color: '#64748B' }}>✕</span>
              <span>Exit Preview</span>
            </button>

            <span
              style={{
                width: 1,
                height: 14,
                background: 'rgba(0, 0, 0, 0.1)',
                margin: '0 2px'
              }}
            />

            <span style={{ color: '#64748B', fontSize: '10.5px' }}>
              Drag buildings to redirect air flow live · Drag background to Orbit · Shift+Drag to Pan
            </span>
          </>
        ) : (
          <>
            <button
              id="tool-btn-navigate"
              type="button"
              onClick={() => dispatch(setActiveTool('navigate'))}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 8px',
                borderRadius: 4,
                border: (activeTool === 'navigate' || activeTool === 'select') ? '1px solid #2563EB' : '1px solid transparent',
                background: (activeTool === 'navigate' || activeTool === 'select') ? '#EFF6FF' : 'transparent',
                color: (activeTool === 'navigate' || activeTool === 'select') ? '#1D4ED8' : '#64748B',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: (activeTool === 'navigate' || activeTool === 'select') ? 600 : 500
              }}
              title="Camera navigation / Click to select / Drag to move"
            >
              <span style={{ fontSize: '12px' }}>✥</span>
              <span>Navigate</span>
            </button>

            <span
              style={{
                width: 1,
                height: 14,
                background: 'rgba(0, 0, 0, 0.1)',
                margin: '0 2px'
              }}
            />

            {/* Domain Box Visibility Checkbox */}
            <label
              id="hud-domain-box-label"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 6px',
                borderRadius: 4,
                border: showDomainBox ? '1px solid #93C5FD' : '1px solid transparent',
                background: showDomainBox ? '#EFF6FF' : 'transparent',
                color: showDomainBox ? '#1D4ED8' : '#64748B',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: showDomainBox ? 600 : 500,
                userSelect: 'none'
              }}
              title="Toggle computational domain bounding box visibility"
            >
              <input
                id="hud-domain-box-checkbox"
                type="checkbox"
                checked={showDomainBox}
                onChange={(e) => dispatch(setShowDomainBox(e.target.checked))}
                style={{ cursor: 'pointer', margin: 0 }}
              />
              <span>Domain Box</span>
            </label>

            {/* Authoritative CFD Navigation & Inspection Controls (Gate 4.2) */}
            {displayRun && (
              <>
                <span
                  style={{
                    width: 1,
                    height: 14,
                    background: 'rgba(0, 0, 0, 0.1)',
                    margin: '0 2px'
                  }}
                />

                {/* CFD Field Visibility Checkbox */}
                <label
                  id="toolbar-cfd-visibility-label"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 6px',
                    borderRadius: 4,
                    border: cfdVisible ? '1px solid #86EFAC' : '1px solid transparent',
                    background: cfdVisible ? '#F0FDF4' : 'transparent',
                    color: cfdVisible ? '#15803D' : '#64748B',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: cfdVisible ? 600 : 500,
                    userSelect: 'none'
                  }}
                  title="Toggle authoritative CFD velocity field visibility"
                >
                  <input
                    id="toolbar-cfd-visibility-checkbox"
                    type="checkbox"
                    checked={cfdVisible}
                    onChange={(e) => dispatch(setCfdVisible(e.target.checked))}
                    style={{ cursor: 'pointer', margin: 0 }}
                  />
                  <span>CFD Field</span>
                </label>

                {/* Vector Density Decimation (Gate 4.2) */}
                <div
                  id="toolbar-cfd-density-control"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '1px 4px',
                    fontSize: '11px',
                    color: '#475569'
                  }}
                  title="Visual vector density decimation"
                >
                  <span style={{ fontSize: '10px', color: '#64748B' }}>Density:</span>
                  <select
                    id="toolbar-cfd-density-select"
                    value={cfdDensity}
                    onChange={(e) => dispatch(setCfdDensity(parseFloat(e.target.value)))}
                    style={{
                      fontSize: '11px',
                      padding: '2px 4px',
                      borderRadius: 3,
                      border: '1px solid #CBD5E1',
                      background: '#FFFFFF',
                      color: '#0F172A',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    <option value={1.0}>100%</option>
                    <option value={0.5}>50%</option>
                    <option value={0.25}>25%</option>
                  </select>
                </div>

                {/* Vector Glyph Scale (Gate 4.2) */}
                <div
                  id="toolbar-cfd-scale-control"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '1px 4px',
                    fontSize: '11px',
                    color: '#475569'
                  }}
                  title="Visual vector glyph length scale"
                >
                  <span style={{ fontSize: '10px', color: '#64748B' }}>Scale:</span>
                  <select
                    id="toolbar-cfd-scale-select"
                    value={cfdGlyphScale}
                    onChange={(e) => dispatch(setCfdGlyphScale(parseFloat(e.target.value)))}
                    style={{
                      fontSize: '11px',
                      padding: '2px 4px',
                      borderRadius: 3,
                      border: '1px solid #CBD5E1',
                      background: '#FFFFFF',
                      color: '#0F172A',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    <option value={0.5}>0.5×</option>
                    <option value={1.0}>1.0×</option>
                    <option value={2.0}>2.0×</option>
                  </select>
                </div>

                {/* CFD Field Probe Mode Toggle (Gate 3.1) */}
                <span
                  style={{
                    width: 1,
                    height: 14,
                    background: 'rgba(0, 0, 0, 0.1)',
                    margin: '0 2px'
                  }}
                />
                <button
                  id="toolbar-probe-mode-btn"
                  type="button"
                  onClick={() => dispatch(toggleProbeMode())}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 4,
                    border: isProbeModeActive ? '1px solid #D97706' : '1px solid transparent',
                    background: isProbeModeActive ? '#FEF3C7' : 'transparent',
                    color: isProbeModeActive ? '#B45309' : '#64748B',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: isProbeModeActive ? 600 : 500
                  }}
                  title="Toggle CFD Point Probe Mode (Click in viewport to probe velocity field)"
                >
                  <span>📍</span>
                  <span>Probe</span>
                  {isProbeModeActive && (
                    <span
                      style={{
                        fontSize: '9px',
                        fontWeight: 700,
                        background: '#F59E0B',
                        color: '#FFFFFF',
                        padding: '0 4px',
                        borderRadius: 2
                      }}
                    >
                      ACTIVE
                    </span>
                  )}
                </button>

                {/* Interactive Flow Preview Mode Toggle (Visualization Experiment 01) */}
                <span
                  style={{
                    width: 1,
                    height: 14,
                    background: 'rgba(0, 0, 0, 0.1)',
                    margin: '0 2px'
                  }}
                />
                <button
                  id="toolbar-flow-preview-btn"
                  type="button"
                  onClick={() => dispatch(toggleFlowPreview())}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 4,
                    border: '1px solid #0284C7',
                    background: '#E0F2FE',
                    color: '#0369A1',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 600
                  }}
                  title="Toggle Interactive Flow Preview Mode (Real-time urban wind laboratory)"
                >
                  <span style={{ fontSize: '13px' }}>≋</span>
                  <span>Flow Preview</span>
                </button>
              </>
            )}

            <span
              style={{
                width: 1,
                height: 14,
                background: 'rgba(0, 0, 0, 0.1)',
                margin: '0 2px'
              }}
            />

            <span style={{ color: '#94A3B8', fontSize: '10px' }}>
              {selectedGeometryId
                ? 'Drag geometry to move · Click to select · Drag background to Orbit'
                : 'Click geometry to select · Drag to Orbit · Shift+Drag to Pan'}
            </span>
          </>
        )}
      </div>

      {/* Interactive Flow Preview Floating Banner (Non-Authoritative Indicator) */}
      {flowPreviewActive && (
        <div
          id="viewport-flow-preview-banner"
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(2, 132, 199, 0.35)',
            borderRadius: 20,
            padding: '5px 16px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
            color: '#0369A1',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            userSelect: 'none',
            pointerEvents: 'none'
          }}
        >
          <span style={{ fontSize: '14px' }}>≋</span>
          <span>INTERACTIVE FLOW PREVIEW</span>
        </div>
      )}

      {/* Authoritative CFD Field Origin HUD Badge / Flow Preview Badge */}
      <div
        id="viewport-cfd-field-origin-badge"
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10,
          background: flowPreviewActive ? 'rgba(255, 255, 255, 0.96)' : displayRun ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)',
          border: flowPreviewActive ? '1px solid #0284C7' : displayRun ? '1px solid #16a34a' : '1px solid rgba(148, 163, 184, 0.35)',
          borderRadius: 4,
          padding: '8px 12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          fontFamily: 'ui-monospace, monospace',
          pointerEvents: 'none',
          minWidth: 150
        }}
      >
        {flowPreviewActive ? (
          <div>
            <div style={{ fontSize: '9px', fontWeight: 700, color: '#0369A1', letterSpacing: '0.08em' }}>
              SIMULATION MODE
            </div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#0F172A', marginTop: 1 }}>
              INTERACTIVE FLOW PREVIEW
            </div>
            {displayRun && (
              <div style={{ fontSize: '9.5px', color: '#64748B', marginTop: 5, paddingTop: 4, borderTop: '1px solid #E2E8F0', lineHeight: 1.4 }}>
                <div><strong>Base Run:</strong> {displayRun.runId}</div>
              </div>
            )}
          </div>
        ) : displayRun ? (
          <div>
            <div style={{ fontSize: '9px', fontWeight: 700, color: '#16a34a', letterSpacing: '0.08em' }}>
              FIELD SOURCE
            </div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#0f172a' }}>
              AUTHORITATIVE CFD
            </div>
            <div style={{ fontSize: '9.5px', color: '#475569', marginTop: 4, lineHeight: 1.4 }}>
              <div><strong>Engine:</strong> {displayRun.engine.toUpperCase()}</div>
              <div><strong>Run:</strong> {displayRun.runId}</div>
              <div><strong>Status:</strong> {displayRun.status}</div>
              {displayRun.resultSummary?.pointCount ? (
                <div><strong>Vectors:</strong> {displayRun.resultSummary.pointCount.toLocaleString()}</div>
              ) : null}
              {/* Velocity Magnitude Analysis (Gate 4.1) */}
              {diagnostics && diagnostics.runId === displayRun.runId && diagnostics.pointCount > 0 ? (
                <div
                  id="viewport-velocity-magnitude-analysis"
                  style={{
                    marginTop: 6,
                    paddingTop: 6,
                    borderTop: '1px solid #e2e8f0',
                    color: '#0f172a'
                  }}
                >
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#2563eb', letterSpacing: '0.06em' }}>
                    VELOCITY MAGNITUDE |U|
                  </div>
                  <div style={{ fontSize: '9px', color: '#334155', marginTop: 2, lineHeight: 1.4 }}>
                    <div><strong>Min:</strong> {diagnostics.magnitude.min.toFixed(3)} m/s</div>
                    <div><strong>Max:</strong> {diagnostics.magnitude.max.toFixed(3)} m/s</div>
                    <div><strong>Mean:</strong> {diagnostics.magnitude.mean.toFixed(3)} m/s</div>
                    <div><strong>Samples:</strong> {diagnostics.pointCount.toLocaleString()}</div>
                  </div>
                </div>
              ) : displayRun.resultSummary?.minSpeed !== undefined && displayRun.resultSummary?.maxSpeed !== undefined ? (
                <div
                  id="viewport-velocity-magnitude-analysis"
                  style={{
                    marginTop: 6,
                    paddingTop: 6,
                    borderTop: '1px solid #e2e8f0',
                    color: '#0f172a'
                  }}
                >
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#2563eb', letterSpacing: '0.06em' }}>
                    VELOCITY MAGNITUDE |U|
                  </div>
                  <div style={{ fontSize: '9px', color: '#334155', marginTop: 2, lineHeight: 1.4 }}>
                    <div><strong>Min:</strong> {displayRun.resultSummary.minSpeed.toFixed(3)} m/s</div>
                    <div><strong>Max:</strong> {displayRun.resultSummary.maxSpeed.toFixed(3)} m/s</div>
                    {displayRun.resultSummary.meanSpeed !== undefined && (
                      <div><strong>Mean:</strong> {displayRun.resultSummary.meanSpeed.toFixed(3)} m/s</div>
                    )}
                    <div><strong>Samples:</strong> {displayRun.resultSummary.pointCount.toLocaleString()}</div>
                  </div>
                </div>
              ) : null}
              {activeRun && activeRun.runId !== displayRun.runId && (
                <div
                  style={{
                    marginTop: 4,
                    padding: '2px 4px',
                    background: activeRun.status === 'FAILED' ? '#FEF2F2' : '#F1F5F9',
                    border: activeRun.status === 'FAILED' ? '1px solid #FECACA' : '1px solid #CBD5E1',
                    borderRadius: 3,
                    color: activeRun.status === 'FAILED' ? '#991B1B' : '#475569',
                    fontSize: '8px',
                    lineHeight: 1.3
                  }}
                >
                  {activeRun.status === 'FAILED'
                    ? `Active Run ${activeRun.runId} FAILED. Showing last valid result (${displayRun.runId}).`
                    : `Active Run ${activeRun.runId} (${activeRun.status}). Showing last valid result.`}
                </div>
              )}
              <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #e2e8f0', color: '#64748b', fontSize: '8.5px' }}>
                <div><strong>Domain:</strong> {cfdSetup?.domain ? `${cfdSetup.domain.width} × ${cfdSetup.domain.length} × ${cfdSetup.domain.height} m` : '100 × 100 × 30 m'}</div>
                <div><strong>Box:</strong> {showDomainBox ? 'Visible' : 'Hidden'}</div>
                <div><strong>CFD Field:</strong> {cfdVisible ? 'Visible' : 'Hidden'} ({Math.round(cfdDensity * 100)}%, {cfdGlyphScale}×)</div>
              </div>
              {/* Active CFD Probe HUD Display (Gate 3.1) */}
              {activeProbe && activeProbe.status === 'FOUND' && activeProbe.sample ? (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #fed7aa', color: '#7c2d12' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#c2410c', letterSpacing: '0.06em' }}>
                    PROBE (Nearest CFD sample)
                  </div>
                  <div style={{ fontSize: '10.5px', fontWeight: 700, color: '#9a3412', marginTop: 2 }}>
                    |U| = {activeProbe.sample.velocity.magnitude.toFixed(3)} m/s
                  </div>
                  <div style={{ fontSize: '9px', color: '#7c2d12', marginTop: 1 }}>
                    Ux: {activeProbe.sample.velocity.ux.toFixed(3)} | Uy: {activeProbe.sample.velocity.uy.toFixed(3)} | Uz: {activeProbe.sample.velocity.uz.toFixed(3)} m/s
                  </div>
                  <div style={{ fontSize: '8.5px', color: '#9a3412', marginTop: 1 }}>
                    Pos: [{activeProbe.sample.position.x.toFixed(1)}, {activeProbe.sample.position.y.toFixed(1)}, {activeProbe.sample.position.z.toFixed(1)}] m
                  </div>
                </div>
              ) : activeProbe && activeProbe.status === 'OUTSIDE_FIELD' ? (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #fecaca', color: '#b91c1c', fontSize: '9px' }}>
                  No CFD sample available at this location.
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>
            NO CFD RESULT
          </div>
        )}
      </div>

      {/* Velocity-Magnitude Visual Legend (Gate 4.2 / Flow Preview) */}
      {flowPreviewActive ? (
        <div
          id="viewport-flow-preview-legend"
          style={{
            position: 'absolute',
            bottom: 48,
            right: 12,
            zIndex: 10,
            background: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
            padding: '8px 12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            fontFamily: 'ui-monospace, monospace',
            minWidth: 165,
            pointerEvents: 'none'
          }}
        >
          <div style={{ fontSize: '9px', fontWeight: 700, color: '#0369A1', letterSpacing: '0.06em' }}>
            FLOW SPEED (PREVIEW)
          </div>
          <div style={{ fontSize: '8.5px', color: '#64748B', marginBottom: 4 }}>
            Relative to ambient wind (1.0×)
          </div>

          {/* Aerodynamic air flow gradient matched to authoritative vector color mapping */}
          <div
            style={{
              height: 8,
              width: '100%',
              borderRadius: 2,
              background: AIRFLOW_LEGEND_GRADIENT,
              marginBottom: 4,
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#334155', fontWeight: 600 }}>
            <span>Wake ({AIRFLOW_MIN_SPEED_RATIO}×)</span>
            <span>Extreme ({AIRFLOW_MAX_SPEED_RATIO}×)</span>
          </div>
        </div>
      ) : displayRun && cfdVisible ? (
        <div
          id="viewport-velocity-magnitude-legend"
          style={{
            position: 'absolute',
            bottom: 48,
            right: 12,
            zIndex: 10,
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
            borderRadius: 4,
            padding: '8px 12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            fontFamily: 'ui-monospace, monospace',
            minWidth: 155,
            pointerEvents: 'none'
          }}
        >
          <div style={{ fontSize: '9px', fontWeight: 700, color: '#1e40af', letterSpacing: '0.06em' }}>
            VELOCITY MAGNITUDE
          </div>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: 4 }}>
            m/s
          </div>

          {/* Scientific continuous color bar matching WebGL2 renderer */}
          <div
            style={{
              height: 8,
              width: '100%',
              borderRadius: 2,
              background: 'linear-gradient(to right, #0d4cb3 0%, #0dc2e6 25%, #33cc55 50%, #e6b80d 75%, #e6260d 100%)',
              marginBottom: 4,
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#0f172a', fontWeight: 600 }}>
            <span>Min: {(diagnostics?.runId === displayRun.runId ? diagnostics.magnitude.min : displayRun.resultSummary?.minSpeed ?? 0).toFixed(3)}</span>
            <span>Max: {(diagnostics?.runId === displayRun.runId ? diagnostics.magnitude.max : displayRun.resultSummary?.maxSpeed ?? 0).toFixed(3)}</span>
          </div>
          {(diagnostics?.runId === displayRun.runId ? diagnostics.magnitude.mean : displayRun.resultSummary?.meanSpeed) !== undefined && (
            <div style={{ fontSize: '8.5px', color: '#475569', marginTop: 2, textAlign: 'center' }}>
              Mean: {(diagnostics?.runId === displayRun.runId ? diagnostics.magnitude.mean : displayRun.resultSummary?.meanSpeed ?? 0).toFixed(3)} m/s
            </div>
          )}
        </div>
      ) : null}

      {/* Interactive Flow Preview Bottom Control Bar (Gate 03) */}
      {flowPreviewActive && (
        <div
          id="viewport-flow-preview-bottom-bar"
          style={{
            position: 'absolute',
            bottom: 14,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(2, 132, 199, 0.3)',
            borderRadius: 6,
            padding: '6px 14px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        >
          {/* Header Tag */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', userSelect: 'none' }}>
            <span style={{ fontSize: '9px', fontWeight: 800, color: '#0369A1', letterSpacing: '0.06em' }}>
              PREVIEW WIND
            </span>
          </div>

          <span style={{ width: 1, height: 22, background: 'rgba(0, 0, 0, 0.1)' }} />

          {/* Speed Slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label
              htmlFor="preview-wind-speed-slider"
              style={{ fontSize: '11px', color: '#475569', fontWeight: 600, userSelect: 'none' }}
            >
              Speed
            </label>
            <input
              id="preview-wind-speed-slider"
              type="range"
              min="1.0"
              max="25.0"
              step="0.5"
              value={previewWindSpeed}
              onChange={(e) => setPreviewWindSpeed(parseFloat(e.target.value))}
              style={{ width: 85, cursor: 'pointer', accentColor: '#0284C7' }}
              title={`Preview Wind Speed: ${previewWindSpeed.toFixed(1)} m/s`}
            />
            <span
              id="preview-wind-speed-display"
              style={{
                fontSize: '11px',
                fontWeight: 700,
                fontFamily: 'ui-monospace, monospace',
                color: '#0F172A',
                minWidth: 48,
                textAlign: 'right'
              }}
            >
              {previewWindSpeed.toFixed(1)} m/s
            </span>
          </div>

          <span style={{ width: 1, height: 22, background: 'rgba(0, 0, 0, 0.1)' }} />

          {/* Direction Slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label
              htmlFor="preview-wind-dir-slider"
              style={{ fontSize: '11px', color: '#475569', fontWeight: 600, userSelect: 'none' }}
            >
              Direction
            </label>
            <input
              id="preview-wind-dir-slider"
              type="range"
              min="0"
              max="359"
              step="1"
              value={Math.round(previewWindDirection)}
              onChange={(e) => setPreviewWindDirection(parseFloat(e.target.value))}
              style={{ width: 85, cursor: 'pointer', accentColor: '#0284C7' }}
              title={`Preview Wind Direction: ${Math.round(previewWindDirection)}°`}
            />
            <span
              id="preview-wind-dir-display"
              style={{
                fontSize: '11px',
                fontWeight: 700,
                fontFamily: 'ui-monospace, monospace',
                color: '#0F172A',
                minWidth: 32,
                textAlign: 'right'
              }}
            >
              {Math.round(previewWindDirection)}°
            </span>
          </div>

          <span style={{ width: 1, height: 22, background: 'rgba(0, 0, 0, 0.1)' }} />

          {/* Flow Density Slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label
              htmlFor="preview-flow-density-slider"
              style={{ fontSize: '11px', color: '#475569', fontWeight: 600, userSelect: 'none', whiteSpace: 'nowrap' }}
              title="Number of visible airflow streamlets (visual tracers)"
            >
              Flow Density
            </label>
            <input
              id="preview-flow-density-slider"
              type="range"
              min="300"
              max="5000"
              step="100"
              value={previewFlowDensity}
              onChange={(e) => setPreviewFlowDensity(parseInt(e.target.value, 10))}
              style={{ width: 85, cursor: 'pointer', accentColor: '#0284C7' }}
              title={`Flow Density: ${previewFlowDensity.toLocaleString()} visual tracers`}
            />
            <span
              id="preview-flow-density-display"
              style={{
                fontSize: '11px',
                fontWeight: 700,
                fontFamily: 'ui-monospace, monospace',
                color: '#0F172A',
                minWidth: 42,
                textAlign: 'right'
              }}
            >
              {previewFlowDensity.toLocaleString()}
            </span>
          </div>

          <span style={{ width: 1, height: 22, background: 'rgba(0, 0, 0, 0.1)' }} />

          {/* Add Building Button */}
          <button
            id="preview-add-building-btn"
            type="button"
            onClick={handleAddBuilding}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
              borderRadius: 4,
              border: '1px solid #0284C7',
              background: '#E0F2FE',
              color: '#0369A1',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 600,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              whiteSpace: 'nowrap'
            }}
            title="Add a new building obstacle to interactive flow preview"
          >
            <span style={{ fontSize: '12px', fontWeight: 700 }}>+</span>
            <span>Add Building</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewportCanvas;
